// Visitor profile with policy-scoped history (PB-03-01, PB-03-05) and the host directory (PB-03-02, OR-08).
import type { HostResponse, PersonHistoryResponse } from '@passage/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, NavLink, useParams } from 'react-router';
import { ApiRequestError, api, get } from '../api.ts';
import { useSiteName, useWorkingSite } from '../components/Shell.tsx';
import { Dialog, ErrorState, ErrorSummary, Field, Loading, PageHead, StatusBadge } from '../components/ui.tsx';
import { StateBadge } from '../components/visit-ui.tsx';
import { useI18n } from '../i18n.tsx';
import { useSession } from '../session.tsx';
import { HostFieldsDialog, ProfileFieldsPanel } from '../components/people-ui.tsx';
import { PersonAffiliationsPanel, PersonLinksPanel } from '../components/orgs-ui.tsx'; // ADR-028
import { DepartmentSelect } from '../components/department-ui.tsx'; // PB-18-07

export function PersonPage() {
  const { personId } = useParams();
  const { t, tk, date, dateTime } = useI18n();
  const { scopeKey } = useSession();
  const siteName = useSiteName();
  const q = useQuery({ queryKey: [...scopeKey, 'person', personId], queryFn: () => get<PersonHistoryResponse>(`/api/v1/people/${personId}`) });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} />;
  const { person, visits, partialScope, provenance } = q.data;
  // PB-03-01 (PD-071): where the current value came from (source and date, never earlier values).
  const source = (f: keyof typeof provenance) => {
    const p = provenance[f];
    return p ? <small className="tiny" style={{ display: 'block' }}>{t('person.source', { source: tk(`person.source.${p.source}`), date: date(p.at, { day: 'numeric', month: 'short', year: 'numeric' }) })}</small> : null;
  };
  return (
    <>
      <PageHead eyebrow={t('person.title')} title={[person.givenName, person.familyName].filter(Boolean).join(' ')} />
      <div className="cols">
        <section className="panel" aria-labelledby="history">
          <div className="panel-head">
            <h2 id="history">{t('person.history')}</h2>
          </div>
          {partialScope ? <p className="pad note" style={{ margin: 16 }}>{t('person.partial')}</p> : null}
          {visits.length === 0 ? (
            <div className="empty">
              <p>{t('person.none')}</p>
            </div>
          ) : (
            <div className="tablewrap" tabIndex={0} aria-label={t('person.history')}>
              <table>
                <thead>
                  <tr>
                    <th scope="col">{t('visits.col.visit')}</th>
                    <th scope="col">{t('visit.f.site')}</th>
                    <th scope="col">{t('visits.col.planning')}</th>
                    <th scope="col">{t('visit.col.approval')}</th>
                    <th scope="col">{t('visit.col.presence')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visits.map((v) => (
                    <tr key={v.visitId}>
                      <td>
                        <Link to={`/visits/${v.visitId}`}>
                          <strong>{v.title ?? t('visits.untitled')}</strong>
                        </Link>
                        <small>
                          <span data-ltr>{v.reference}</span>
                          {v.startsAt ? ` · ${dateTime(v.startsAt)}` : ''}
                        </small>
                      </td>
                      <td>{siteName(v.siteId)}</td>
                      <td>
                        <StateBadge domain="planning" value={v.planningStatus} />
                      </td>
                      <td>
                        <StateBadge domain="approval" value={v.approvalStatus} />
                      </td>
                      <td>
                        <StateBadge domain="presence" value={v.presenceStatus} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <aside className="panel" aria-labelledby="contact">
          <div className="panel-head">
            <h2 id="contact">{t('person.contact')}</h2>
          </div>
          <div className="pad">
            <dl className="definition" style={{ gridTemplateColumns: '1fr' }}>
              <div>
                <dt>{t('person.organisation')}</dt>
                <dd>{person.organisation?.name ?? t('common.none')}</dd>
              </div>
              <div>
                <dt>{t('vform.email')}</dt>
                <dd data-ltr>{person.email ?? t('common.none')}</dd>
                {source('email')}
              </div>
              <div>
                <dt>{t('vform.phone')}</dt>
                <dd data-ltr>{person.phone ?? t('common.none')}</dd>
                {source('phone')}
              </div>
            </dl>
            {person.masked ? <p className="tiny" style={{ marginBlockStart: 12 }}>{t('visit.masked')}</p> : null}
          </div>
        </aside>
      </div>
      {/* OR-07 / PB-03-07 (people & records stream): configured fields, masking and reveals. */}
      <ProfileFieldsPanel personId={person.id} />
      {/* PB-03-03 / PB-03-04 (people & organisations stream): affiliations and link review. */}
      <PersonAffiliationsPanel personId={person.id} />
      <PersonLinksPanel personId={person.id} />
    </>
  );
}

/** Host directory for organisation administrators (Users & permissions → Host directory). */
export function HostDirectory() {
  const { t, tk } = useI18n();
  const { scopeKey, session } = useSession();
  const [siteId] = useWorkingSite();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const q = useQuery({ queryKey: [...scopeKey, 'hosts', siteId, 'all'], enabled: !!siteId, queryFn: () => get<{ items: HostResponse[] }>(`/api/v1/hosts?siteId=${siteId}&includeInactive=true`) });
  const toggle = useMutation({
    mutationFn: (h: HostResponse) => api<HostResponse>('PATCH', `/api/v1/hosts/${h.id}`, { expectedVersion: h.version, status: h.status === 'active' ? 'inactive' : 'active' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...scopeKey, 'hosts'] }),
  });
  const impact = toggle.error instanceof ApiRequestError && toggle.error.code === 'IMPACT_REVIEW_REQUIRED' ? ((toggle.error.details.visits as Array<{ id: string; reference: string; title: string | null }>) ?? []) : [];
  // PB-03-02 (people & records stream): deactivate and route future visits to reassignment.
  const [fieldsOf, setFieldsOf] = useState<HostResponse | null>(null);
  const reassign = useMutation({
    mutationFn: (h: HostResponse) => api('POST', `/api/v1/hosts/${h.id}/deactivate`, { expectedVersion: h.version, reason: t('ppl.hosts.reassignReason') }),
    onSuccess: () => {
      toggle.reset();
      void qc.invalidateQueries({ queryKey: [...scopeKey, 'hosts'] });
    },
  });
  return (
    <section className="panel" aria-labelledby="hosts-title">
      <div className="panel-head">
        <div>
          <h2 id="hosts-title">{t('hosts.title')}</h2>
          <p>{t('hosts.sub')}</p>
        </div>
        <button className="button primary" type="button" onClick={() => setAdding(true)}>
          {t('hosts.add')}
        </button>
      </div>
      {toggle.error ? (
        <div className="pad" style={{ paddingBlockEnd: 0 }}>
          <ErrorSummary error={toggle.error} fieldIds={{}} />
          {impact.length ? (
            <ul className="problems">
              {impact.map((v) => (
                <li key={v.id}>
                  <span data-ltr>{v.reference}</span> {v.title ?? ''}
                </li>
              ))}
            </ul>
          ) : null}
          {impact.length && toggle.variables ? (
            <div className="actions">
              <button className="button small danger" type="button" disabled={reassign.isPending} onClick={() => reassign.mutate(toggle.variables!)}>
                {t('ppl.hosts.deactivateReassign')}
              </button>
              <ErrorSummary error={reassign.error} fieldIds={{}} />
            </div>
          ) : null}
        </div>
      ) : null}
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <div className="pad">
          <ErrorState error={q.error} />
        </div>
      ) : q.data.items.length === 0 ? (
        <div className="empty">
          <p>{t('hosts.empty')}</p>
        </div>
      ) : (
        <div className="tablewrap" tabIndex={0} aria-label={t('hosts.title')} data-scroll-hint={t('common.scrollHint')}>
          <table>
            <thead>
              <tr>
                <th scope="col">{t('hosts.col.host')}</th>
                <th scope="col">{t('hosts.col.department')}</th>
                <th scope="col">{t('hosts.col.employee')}</th>
                <th scope="col">{t('hosts.col.account')}</th>
                <th scope="col">{t('hosts.col.status')}</th>
                <th scope="col">{t('users.col.action')}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((h) => (
                <tr key={h.id}>
                  <td>
                    <strong>{h.displayName}</strong>
                    <small>
                      {h.email ? <span data-ltr>{h.email}</span> : null}
                      {h.email ? ' · ' : ''}
                      {tk(`hosts.source.${h.source}`)}
                    </small>
                  </td>
                  <td>
                    {h.department ?? '—'}
                    {h.jobTitle ? <small>{h.jobTitle}</small> : null}
                  </td>
                  <td data-ltr>{h.employeeNumber ?? '—'}</td>
                  <td>{h.userId ? t('hosts.linked') : t('hosts.notLinked')}</td>
                  <td>
                    <StatusBadge status={h.status} />
                  </td>
                  <td>
                    <button className="button small" type="button" disabled={toggle.isPending} onClick={() => toggle.mutate(h)}>
                      {h.status === 'active' ? t('hosts.deactivate') : t('hosts.reactivate')}
                    </button>{' '}
                    <button className="button small quiet" type="button" onClick={() => setFieldsOf(h)}>
                      {t('ppl.hosts.fields')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding ? <AddHostDialog onClose={() => setAdding(false)} sites={session.sites} /> : null}
      {fieldsOf ? <HostFieldsDialog hostId={fieldsOf.id} name={fieldsOf.displayName} onClose={() => setFieldsOf(null)} /> : null}
    </section>
  );
}

function AddHostDialog({ onClose, sites }: { onClose: () => void; sites: ReadonlyArray<{ id: string; name: string }> }) {
  const { t } = useI18n();
  const { scopeKey } = useSession();
  const qc = useQueryClient();
  const [f, setF] = useState({ displayName: '', email: '', employeeNumber: '', department: '', jobTitle: '', officeSiteId: '', mobilePhone: '' });
  const m = useMutation({
    mutationFn: () =>
      api<HostResponse>('POST', '/api/v1/hosts', {
        displayName: f.displayName,
        email: f.email.trim() || null,
        employeeNumber: f.employeeNumber.trim() || null,
        departmentId: f.department || null, // PB-18-07 (PD-541): a department reference
        jobTitle: f.jobTitle || null,
        officeSiteId: f.officeSiteId || null,
        mobilePhone: f.mobilePhone.trim() || null, // PB-09-14 (PD-530)
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...scopeKey, 'hosts'] });
      onClose();
    },
  });
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  const field = (k: keyof typeof f, label: string, ltr = false, hint?: string) => (
    <Field id={`host-${k}`} label={label} error={fe[k]} {...(hint ? { hint } : {})}>
      {(p) => <input {...p} {...(ltr ? { dir: 'ltr' } : {})} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />}
    </Field>
  );
  return (
    <Dialog open title={t('hosts.add')} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorSummary error={m.error} fieldIds={{ displayName: 'host-displayName', email: 'host-email', employeeNumber: 'host-employeeNumber', mobilePhone: 'host-mobilePhone' }} />
        <div className="formgrid">
          {field('displayName', t('hosts.displayName'))}
          {field('email', t('hosts.email'), true)}
          {field('employeeNumber', t('hosts.employeeNumber'), true)}
          <DepartmentSelect id="host-department" value={f.department} onChange={(department) => setF({ ...f, department })} />
          {field('jobTitle', t('hosts.jobTitle'))}
          {field('mobilePhone', t('msg.hosts.mobilePhone'), true, t('msg.hosts.mobilePhoneHint'))}
          <Field id="host-office" label={t('visit.f.site')}>
            {(p) => (
              <select {...p} value={f.officeSiteId} onChange={(e) => setF({ ...f, officeSiteId: e.target.value })}>
                <option value="">{t('common.none')}</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="button primary" type="submit" disabled={m.isPending || !f.displayName.trim()}>
            {t('hosts.add')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** Tabs shared by Users & permissions and the host directory (PD-025). */
export function UsersTabs() {
  const { t } = useI18n();
  const { can, canAnywhere } = useSession();
  // ADR-011: approved tabs "Role preview" and "Identity providers" (role-review prototype, users()).
  const roles = can('grant.read') || canAnywhere('user.directory.read');
  const identity = can('identity.read');
  const calendars = can('calendar.read'); // PD-271 (calendars stream)
  if (!can('host.manage') && !roles && !identity && !calendars && !can('record.import') && !can('field.dictionary.manage')) return null;
  return (
    <nav className="tabs" aria-label={t('nav.users')}>
      <NavLink to="/users" end>
        {t('users.panel')}
      </NavLink>
      {can('host.manage') ? <NavLink to="/users/hosts">{t('hosts.tab')}</NavLink> : null}
      {roles ? <NavLink to="/users/roles">{t('roles.tab')}</NavLink> : null}
      {identity ? <NavLink to="/users/identity">{t('identity.tab')}</NavLink> : null}
      {calendars ? <NavLink to="/users/calendars">{t('cal.tab')}</NavLink> : null}
      {/* People & records stream (ADR-023) */}
      {can('host.sync.manage') ? <NavLink to="/users/host-sync">{t('ppl.tab.hostSync')}</NavLink> : null}
      {/* PB-18-07 (PD-540 – PD-546) */}
      {can('host.manage') || can('host.sync.manage') ? <NavLink to="/users/departments">{t('mdl.tab.departments')}</NavLink> : null}
      {can('host.sync.manage') ? <NavLink to="/users/hr-mapping">{t('mdl.tab.hr')}</NavLink> : null}
      {can('record.import') ? <NavLink to="/users/import">{t('ppl.tab.import')}</NavLink> : null}
      {can('field.dictionary.manage') ? <NavLink to="/users/fields">{t('ppl.tab.fields')}</NavLink> : null}
    </nav>
  );
}

export function HostsPage() {
  const { t } = useI18n();
  return (
    <>
      <PageHead eyebrow={t('users.eyebrow')} title={t('users.title')} intro={t('hosts.sub')} />
      <UsersTabs />
      <HostDirectory />
    </>
  );
}
